import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaPlay, FaRocket, FaShieldAlt, FaUsers } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "Welcome to BT India",
      subtitle: "Your Trusted Digital Financial Partner",
      description: "Experience seamless digital transactions, secure wallet management, and comprehensive financial solutions designed for modern India.",
      ctaText: "Get Started",
      ctaLink: "/get-started",
      bgGradient: "from-blue-600 via-purple-600 to-indigo-700",
      icon: <FaRocket className="text-6xl" />,
      image: "/bt-india-logo.jpeg"
    },
    {
      id: 2,
      title: "Secure & Reliable",
      subtitle: "Bank-Grade Security Standards",
      description: "Your financial data is protected with enterprise-level security, encryption, and multi-factor authentication.",
      ctaText: "Learn More",
      ctaLink: "/learn-more",
      bgGradient: "from-green-600 via-emerald-600 to-teal-700",
      icon: <FaShieldAlt className="text-6xl" />,
      image: "/bt-india-logo.jpeg"
    },
    {
      id: 3,
      title: "Multi-Platform Access",
      subtitle: "Available Everywhere You Go",
      description: "Access your account seamlessly across web, Android, and iOS platforms with synchronized data and offline capabilities.",
      ctaText: "Download Apps",
      ctaLink: "/downloads",
      bgGradient: "from-purple-600 via-pink-600 to-rose-700",
      icon: <FaUsers className="text-6xl" />,
      image: "/bt-india-logo.jpeg"
    },
    {
      id: 4,
      title: "24/7 Support",
      subtitle: "Always Here When You Need Us",
      description: "Round-the-clock customer support with real-time assistance, comprehensive help center, and dedicated account managers.",
      ctaText: "Contact Support",
      ctaLink: "/support",
      bgGradient: "from-orange-600 via-red-600 to-pink-700",
      icon: <FaPlay className="text-6xl" />,
      image: "/bt-india-logo.jpeg"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="relative h-screen overflow-hidden">
      {/* Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className={`absolute inset-0 bg-gradient-to-br ${slides[currentSlide].bgGradient}`}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/5 to-transparent"></div>
            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 flex items-center justify-center h-full px-4 sm:px-6 lg:px-8 py-8">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="text-white text-center lg:text-left order-2 lg:order-1"
              >
                <div className="mb-4 lg:mb-6 flex justify-center lg:justify-start">
                  <div className="p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                    {slides[currentSlide].icon}
                  </div>
                </div>
                
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 lg:mb-4 leading-tight">
                  {slides[currentSlide].title}
                </h1>
                
                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold mb-4 lg:mb-6 text-white/90">
                  {slides[currentSlide].subtitle}
                </h2>
                
                <p className="text-base sm:text-lg md:text-xl mb-6 lg:mb-8 text-white/80 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  {slides[currentSlide].description}
                </p>
                
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex justify-center lg:justify-start"
                >
                  <Link
                    to={slides[currentSlide].ctaLink}
                    className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
                  >
                    {slides[currentSlide].ctaText}
                    <FaChevronRight className="ml-2" />
                  </Link>
                </motion.div>
              </motion.div>

              {/* Image/Visual */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="flex justify-center lg:justify-end order-1 lg:order-2 mb-6 lg:mb-0"
              >
                <div className="relative">
                  <div className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl border-4 border-white/30">
                    <img
                      src={slides[currentSlide].image}
                      alt="BT India"
                      className="w-56 h-56 sm:w-72 sm:h-72 lg:w-88 lg:h-88 object-contain rounded-full shadow-2xl"
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                  </div>
                  {/* Floating elements - hidden on mobile */}
                  <div className="hidden sm:block absolute -top-6 -right-6 w-20 h-20 bg-white/30 rounded-full animate-pulse shadow-lg"></div>
                  <div className="hidden sm:block absolute -bottom-6 -left-6 w-16 h-16 bg-white/30 rounded-full animate-pulse delay-1000 shadow-lg"></div>
                  <div className="hidden sm:block absolute top-1/2 -right-12 w-8 h-8 bg-white/20 rounded-full animate-bounce delay-500"></div>
                  <div className="hidden sm:block absolute bottom-1/2 -left-12 w-6 h-6 bg-white/20 rounded-full animate-bounce delay-700"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 z-20 p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all duration-300"
      >
        <FaChevronLeft className="text-white text-lg sm:text-xl" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 z-20 p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all duration-300"
      >
        <FaChevronRight className="text-white text-lg sm:text-xl" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2 sm:space-x-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? 'bg-white scale-125' 
                : 'bg-white/50 hover:bg-white/75'
            }`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white/20 z-20">
        <motion.div
          className="h-full bg-white"
          initial={{ width: 0 }}
          animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          transition={{ duration: 5, ease: "linear" }}
        />
      </div>
    </div>
  );
};

export default HeroSlider; 